import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Funnel, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listEnrollmentRecords } from "@/lib/enrollment";
import {
  STAGES,
  STAGE_META,
  SOURCES,
  SOURCE_LABELS,
  formatChildName,
  formatEnrollmentDate,
  formatPersonName,
  stageTone,
} from "@/lib/enrollmentTypes";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { DEMO_CLASSROOMS } from "@/lib/dailyReports";

// Visible Kanban columns — 'withdrawn' is tucked away in the table-style
// count below the board to keep the board compact.
const BOARD_COLUMNS = [
  "lead",
  "inquiry",
  "tour_scheduled",
  "tour_completed",
  "application_started",
  "application_submitted",
  "waitlisted",
  "offer",
  "enrollment_pending",
  "enrolled",
];

export default function AdminEnrollment() {
  const { profile, role } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classrooms, setClassrooms] = useState(DEMO_CLASSROOMS);

  const [filters, setFilters] = useState({
    classroomId: "",
    source: "",
    search: "",
  });

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    (async () => {
      const { data } = await supabase
        .from("classrooms")
        .select("id, name")
        .order("name");
      if (data) setClassrooms(data);
    })();
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      setLoading(true);
      try {
        const list = await listEnrollmentRecords({
          role,
          profileId: profile.id,
          filters: {
            classroomId: filters.classroomId || undefined,
            source: filters.source || undefined,
            search: filters.search || undefined,
          },
        });
        setRows(list);
      } catch (err) {
        toast.error(err.message || "Could not load enrollment");
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.id, role, filters]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s, []]));
    rows.forEach((r) => {
      if (map[r.stage]) map[r.stage].push(r);
    });
    return map;
  }, [rows]);

  const kpis = useMemo(() => {
    const c = (stage) => byStage[stage]?.length || 0;
    return {
      leads: c("lead") + c("inquiry") + c("tour_scheduled") + c("tour_completed"),
      applications: c("application_started") + c("application_submitted"),
      waitlisted: c("waitlisted"),
      offers: c("offer") + c("accepted") + c("enrollment_pending"),
      enrolled: c("enrolled"),
    };
  }, [byStage]);

  return (
    <div data-testid="admin-enrollment" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Operational
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Enrollment pipeline
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Move prospective families through a clear, reviewable path. Open a
          case to schedule a tour, log follow-up, manage the waitlist, or
          create the operational family record only after an authorized offer.
        </p>
      </section>

      {/* KPIs */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Kpi testId="enr-kpi-inquiries" label="Leads" value={String(kpis.leads)} />
        <Kpi testId="enr-kpi-apps" label="Applications" value={String(kpis.applications)} />
        <Kpi testId="enr-kpi-waitlist" label="Waitlist" value={String(kpis.waitlisted)} />
        <Kpi testId="enr-kpi-accepted" label="Offers" value={String(kpis.offers)} accent />
        <Kpi testId="enr-kpi-enrolled" label="Enrolled" value={String(kpis.enrolled)} accent />
      </section>

      {/* Filters */}
      <section
        data-testid="enr-filters"
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        <div className="mb-4 flex items-center gap-2">
          <Funnel size={18} weight="duotone" className="text-primary-hover" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Filter pipeline
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <FilterSelect
            testId="enr-filter-classroom"
            label="Classroom"
            value={filters.classroomId}
            onChange={(v) => setFilters((f) => ({ ...f, classroomId: v }))}
            options={[
              { value: "", label: "All classrooms" },
              ...classrooms.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <FilterSelect
            testId="enr-filter-source"
            label="Source"
            value={filters.source}
            onChange={(v) => setFilters((f) => ({ ...f, source: v }))}
            options={[
              { value: "", label: "Any source" },
              ...SOURCES.map((s) => ({ value: s, label: SOURCE_LABELS[s] })),
            ]}
          />
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Search family or child
            </span>
            <div className="relative">
              <MagnifyingGlass
                size={14}
                weight="bold"
                className="pointer-events-none absolute left-3 top-3 text-neutral-400"
              />
              <input
                data-testid="enr-filter-search"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder="e.g. Saeed, Noa"
                className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-9 pr-3 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </label>
        </div>
      </section>

      {/* Kanban board */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : (
        <section data-testid="enr-board-section" aria-labelledby="enr-board-title">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 id="enr-board-title" className="font-heading text-base font-semibold text-neutral-900">
              Pipeline stages
            </h2>
            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600">
              <ArrowRight size={13} weight="bold" aria-hidden />
              Scroll horizontally to see all {BOARD_COLUMNS.length} stages
            </p>
          </div>
          <div
            data-testid="enr-board"
            role="region"
            aria-label="Enrollment pipeline stages"
            tabIndex={0}
            className="relative flex gap-3 overflow-x-auto rounded-2xl border border-neutral-200 bg-neutral-50/60 p-3 pb-4 focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {BOARD_COLUMNS.map((stage) => (
              <StageColumn
                key={stage}
                stage={stage}
                records={byStage[stage] || []}
              />
            ))}
          </div>
        </section>
      )}

      {rows.length > 0 && (
        <p className="text-xs text-neutral-500">
          {rows.length} record{rows.length === 1 ? "" : "s"} · Move through
          stages from the detail view.
        </p>
      )}
    </div>
  );
}

function StageColumn({ stage, records }) {
  const meta = STAGE_META[stage];
  const tone = stageTone(stage);
  const Icon = meta.icon;
  return (
    <div
      data-testid={`enr-column-${stage}`}
      className="flex w-72 flex-shrink-0 flex-col gap-2"
    >
      <div className="flex items-center justify-between rounded-2xl border border-neutral-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone.bg}`}>
            <Icon size={14} weight="duotone" className={tone.color} />
          </span>
          <p className="font-heading text-sm font-semibold text-neutral-900">
            {meta.short}
          </p>
        </div>
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
          {records.length}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {records.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-neutral-200 p-3 text-center text-[11px] text-neutral-400">
            Empty
          </div>
        ) : (
          records.map((r) => <PipelineCard key={r.id} record={r} tone={tone} />)
        )}
      </div>
    </div>
  );
}

function PipelineCard({ record, tone }) {
  return (
    <Link
      to={`/admin/enrollment/${record.id}`}
      data-testid={`enr-card-${record.id}`}
      data-stage={record.stage}
      className={`block rounded-2xl border-l-4 ${tone.ring.replace("border-", "border-l-")} border border-neutral-200 bg-white p-3 transition-all hover:-translate-y-[1px] hover:shadow-sm`}
    >
      <p className="font-heading text-sm font-semibold tracking-tight text-neutral-900">
        {formatChildName(record)}
      </p>
      <p className="mt-0.5 text-xs text-neutral-600">
        {formatPersonName(record)}
      </p>
      <p className="mt-2 text-[11px] text-neutral-500">
        {record.classroom?.name || record.preferred_age_group || "No classroom pref"}
        {" · "}
        {formatEnrollmentDate(record.stage_changed_at)}
      </p>
    </Link>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border bg-white p-5 ${accent ? "border-primary/30" : "border-neutral-200"}`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <p
        className={`mt-1 font-heading text-3xl font-semibold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}
      >
        {value}
      </p>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
